import { useLocation } from "react-router-dom";
import useAuth from "@/hooks/useAuth";
import { useDemoGuideStore } from "./useDemoGuideStore";
import { visibleSteps } from "./visibleSteps";

/**
 * Which sidebar destination the guided demo is currently pointing at.
 *
 * The run moves between pages, so the marker has to move with it: Watchlists
 * while they are being sent to build one, then Dashboard the moment they have
 * built it — because at that point the thing to do is back where they came
 * from, and a builder page with no way out is where people stop.
 *
 * Read by SideBar and the builder page, both main-window only. Nothing under
 * src/widget-components/** may import this; popouts render those.
 */
export type DemoNavTarget = "/watchlists-filters" | "/dashboard" | null;

export function useDemoNavTarget(): DemoNavTarget {
  const { authenticated } = useAuth();
  const { pathname } = useLocation();
  const state = useDemoGuideStore((s) => s.state);

  if (authenticated || !state?.assetClass) return null;
  if (state.completedAt || state.exitedAt) return null;

  const step = visibleSteps(state.assetClass)[state.step];

  // Go and build one.
  if (step?.id === "watchlist") return "/watchlists-filters";

  // Built it, but still standing on the builder page: the next instruction is
  // on the dashboard, so point back there. Once they arrive the marker clears,
  // rather than competing with the control they are meant to click next.
  const onDashboard = pathname === "/" || pathname.startsWith("/dashboard");
  if (step?.id === "highlight" && !onDashboard) return "/dashboard";

  return null;
}

/** True while the builder page should present its demo treatment. */
export function useWatchlistPush(): boolean {
  const { authenticated } = useAuth();
  const state = useDemoGuideStore((s) => s.state);

  if (authenticated || !state?.assetClass) return false;
  if (state.completedAt || state.exitedAt) return false;

  return visibleSteps(state.assetClass)[state.step]?.id === "watchlist";
}
